import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Routes from '../../../routes';
import { DataGrid, PageTitleWithToolTip } from '../../Common';
import { select_commonCellProps, text_commonCellProps } from '../../Common/DataGrid/utils';
import { getDateRangeFilterOperator, getRangeFilterOperator } from '../../Common/DataGrid/FilterComponents';
import { StyledCellLink } from '../../Common/DataGrid/CellComponents';
import { CardHeader } from '@mui/material';
import { isDesktop } from '../../../utils';

const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});

const RecoverCylinderStatusHistoryTable = ({
    assetID,
    title,
    refreshToggle,
}) => {
    const [filters, setFilters] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [refreshToggle]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const filterQuery = fetch(Routes.Asset.RecoverCylinderStatusFilters(assetID));
            const filters = await filterQuery.then(response => response.json());

            setFilters(filters);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching filters:', error);
            setLoading(false);
        }
    };

    const columns = [
        {
            field: 'date',
            headerName: 'Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            valueFormatter: (date) => {
                if (!date) return '';
                date = new Date(date);
                return date.toLocaleDateString('en-GB')
            },
            
        },
        {
            field: 'transferredFromId',
            headerName: 'Transferred From (ID)',
            ...text_commonCellProps,
            renderCell: ({ row }) => (
                <StyledCellLink
                    primaryLink
                    path={`/Inventory/${row.transferredFromId}`}
                    text={row.transferredFromId}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'transferredToId',
            headerName: 'Transferred To (ID)',
            ...text_commonCellProps,
            renderCell: ({ row }) => (
                <StyledCellLink
                    primaryLink
                    path={`/Inventory/${row.transferredToId}`}
                    text={row.transferredToId}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'materialType',
            headerName: 'Material Type',
            ...select_commonCellProps,
        },
        {
            field: 'transferAmount',
            headerName: 'Transfer Amount',
            valueGetter: (value) => value,
            filterOperators: rangeFilterOperator,
            flex: 1,
        },
        {
            field: 'toLocation',
            headerName: 'To Location',
            ...select_commonCellProps,
            renderCell: ({ row }) => (
                isDesktop ? (
                    <StyledCellLink
                        primaryLink
                        path={`/Location/${row.toLocationId}`}
                        text={row.toLocation}
                        from={window.location.pathname}
                    />
                ) : row.toLocation
            )
        },
        {
            field: 'technician',
            headerName: 'Technician',
            ...select_commonCellProps,
        },

        {
            field: 'transferredFrom',
            headerName: 'Transferred From (Name)',
            ...text_commonCellProps,
            initiallyVisible: false,

            renderCell: ({ row }) => (
                <StyledCellLink
                    primaryLink
                    path={`/Inventory/${row.transferredFromId}`}
                    text={row.transferredFrom}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'transferredTo',
            headerName: 'Transferred To (Name)',
            ...text_commonCellProps,
            initiallyVisible: false,
            renderCell: ({ row }) => (
                <StyledCellLink
                    primaryLink
                    path={`/Inventory/${row.transferredToId}`}
                    text={row.transferredTo}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'notes',
            headerName: 'Notes',
            ...text_commonCellProps,
            initiallyVisible: false,
        },
        {
            field: 'transferType',
            headerName: 'Transfer Type',
            ...select_commonCellProps,
            initiallyVisible: false,
        },
    ];

    return (
        <DataGrid
            url={Routes.Asset.GetCylinderStatusHistory(assetID)}
            columns={columns}
            defaultSort="effectiveDate_desc"
            headerContent={
                isDesktop && (
                    <CardHeader
                        title={
                            <PageTitleWithToolTip
                                pageTitle={title}
                            />
                        }
                        sx={{
                            "& .MuiCardHeader-action": { display: "flex" },
                            "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                        }}
                    />
                )
            }
            noResultText="No cylinder-to-cylinder transfers have been logged for this cylinder."
            filterOptions={filters}
            refresh={loading}
            hideExportButton
        />
    );
};

RecoverCylinderStatusHistoryTable.propTypes = {
    assetID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
};

RecoverCylinderStatusHistoryTable.defaultProps = {
    title: 'Cylinder Transfers',
};

export default RecoverCylinderStatusHistoryTable;
